import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Changes() {
  const [board, setBoard] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", type_key: "",
                                      start: "", end: "" });
  const [answers, setAnswers] = useState({});
  const [check, setCheck] = useState(null);
  const [acknowledge, setAcknowledge] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    apiFetch("/changes/board").then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setError("Change board unavailable"); return; }
      setError(null);
      setBoard(body);
      setForm((f) => ({ ...f, type_key: f.type_key || body.change_types[0]?.key || "" }));
      if (selected) {
        const fresh = body.changes.find((c) => c.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    });
  }, [selected]);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
  }, [navigate]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const chosenType = board?.change_types.find((t) => t.key === form.type_key);

  const runCheck = () =>
    apiFetch("/changes/schedule-check", {
      method: "POST",
      body: JSON.stringify({ start: new Date(form.start).toISOString(),
                             end: new Date(form.end).toISOString(),
                             type_key: form.type_key }),
    }).then(async (r) => {
      const out = await r.json();
      if (!r.ok) { setMessage(typeof out.detail === "string" ? out.detail
                                                             : "Could not check that window");
                   setCheck(null); return; }
      setCheck(out);
      setAcknowledge(false);
      setMessage(null);
    });

  const post = (path, body, label, then) =>
    apiFetch(path, { method: "POST", body: JSON.stringify(body) }).then(async (r) => {
      const out = await r.json();
      if (!r.ok) {
        setMessage(`${label} failed — ${typeof out.detail === "string" ? out.detail
                                                                       : JSON.stringify(out.detail)}`);
        return;
      }
      setMessage(`${label} done`);
      if (then) then(out);
      load();
    });

  const raise = () =>
    post("/changes", {
      title: form.title, type_key: form.type_key, description: form.description,
      risk_answers: answers,
      schedule: form.start && form.end
        ? { start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString() }
        : null,
      acknowledge_conflicts: acknowledge,
    }, "Change raised", () => {
      setForm({ ...form, title: "", description: "", start: "", end: "" });
      setAnswers({});
      setCheck(null);
      setAcknowledge(false);
    });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="changes-title">Changes</h1>
          <p className="text-sm text-muted-foreground">
            Raise it, schedule it around the freeze windows, send it for approval.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="changes-calendar-link">
            <Link to="/calendar">Calendar</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="changes-error">{error}</p>}
        {message && <p className="text-sm text-amber-800" data-testid="changes-message">{message}</p>}

        {board?.can_author && (
          <section className="space-y-3" data-testid="change-form">
            <h2 className="text-base md:text-lg font-medium">Raise a change</h2>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="change-title" className="text-sm font-medium">Title</label>
                <Input id="change-title" data-testid="change-title" value={form.title}
                       onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label htmlFor="change-type" className="text-sm font-medium">Change type</label>
                <select id="change-type" data-testid="change-type" value={form.type_key}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        onChange={(e) => { setForm({ ...form, type_key: e.target.value });
                                           setAnswers({}); setCheck(null); }}>
                  {(board.change_types || []).map((t) => (
                    <option key={t.key} value={t.key}>{t.name} ({t.category})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <label htmlFor="change-description" className="text-sm font-medium">
                  What is being changed, and how will you back it out?
                </label>
                <textarea id="change-description" data-testid="change-description" rows={3}
                          className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                          value={form.description}
                          onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label htmlFor="change-start" className="text-sm font-medium">Starts</label>
                <Input id="change-start" type="datetime-local" data-testid="change-start"
                       value={form.start}
                       onChange={(e) => { setForm({ ...form, start: e.target.value });
                                          setCheck(null); }} />
              </div>
              <div className="space-y-1">
                <label htmlFor="change-end" className="text-sm font-medium">Ends</label>
                <Input id="change-end" type="datetime-local" data-testid="change-end"
                       value={form.end}
                       onChange={(e) => { setForm({ ...form, end: e.target.value });
                                          setCheck(null); }} />
              </div>
            </div>

            {(chosenType?.risk_assessment?.questions || []).length > 0 && (
              <div className="border rounded-md p-4 space-y-2" data-testid="change-risk">
                <h3 className="text-sm font-semibold">Risk questions</h3>
                {chosenType.risk_assessment.questions.map((q) => (
                  <div key={q.key} className="flex items-start gap-2">
                    <input id={`risk-${q.key}`} type="checkbox"
                           data-testid={`change-risk-${q.key}`}
                           checked={Boolean(answers[q.key])}
                           onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.checked })} />
                    <label htmlFor={`risk-${q.key}`} className="text-sm">{q.label}</label>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" data-testid="change-schedule-check"
                      disabled={!form.start || !form.end} onClick={runCheck}>
                Check this window
              </Button>
              <Button size="sm" data-testid="change-raise"
                      disabled={!form.title || !form.type_key
                                || (check?.blocking && !acknowledge)}
                      onClick={raise}>
                Raise change
              </Button>
            </div>

            {check && (
              <div className="border rounded-md p-4 space-y-2" data-testid="change-check-result">
                <p className={`text-sm ${check.clear ? "text-emerald-800" : "text-amber-800"}`}
                   data-testid="change-check-advice">
                  {check.advice}
                </p>
                {check.conflicts.length > 0 && (
                  <ul className="text-sm list-disc pl-5" data-testid="change-check-conflicts">
                    {check.conflicts.map((c) => (
                      <li key={c.title}>
                        {c.title} ({c.type}) — {new Date(c.start).toLocaleString()} to{" "}
                        {new Date(c.end).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                )}
                {check.blocking && (
                  <div className="flex items-center gap-2">
                    <input id="change-acknowledge" type="checkbox" checked={acknowledge}
                           data-testid="change-acknowledge"
                           onChange={(e) => setAcknowledge(e.target.checked)} />
                    <label htmlFor="change-acknowledge" className="text-sm">
                      I accept the clash — record that I acknowledged it
                    </label>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {board && (
          <section className="space-y-2 border-t pt-6" data-testid="change-board">
            <h2 className="text-base md:text-lg font-medium">
              Changes ({board.totals.awaiting_approval} awaiting approval,{" "}
              {board.totals.with_conflicts} with a window clash)
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-md">
                <caption className="sr-only">Recorded changes</caption>
                <thead>
                  <tr className="text-left border-b">
                    <th scope="col" className="px-3 py-2">Number</th>
                    <th scope="col" className="px-3 py-2">Title</th>
                    <th scope="col" className="px-3 py-2">State</th>
                    <th scope="col" className="px-3 py-2">Risk</th>
                    <th scope="col" className="px-3 py-2">Scheduled</th>
                    <th scope="col" className="px-3 py-2">Approval</th>
                    <th scope="col" className="px-3 py-2">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {board.changes.map((c) => (
                    <tr key={c.id} className="border-b last:border-0"
                        data-testid={`change-row-${c.number}`}>
                      <th scope="row" className="px-3 py-2 font-mono text-left">{c.number}</th>
                      <td className="px-3 py-2">{c.title}</td>
                      <td className="px-3 py-2">{c.state}</td>
                      <td className="px-3 py-2">
                        {c.risk_level || "unscored"}
                        {c.cab_required && (
                          <span className="ml-1 text-amber-800"
                                data-testid={`change-cab-${c.number}`}>CAB</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {c.schedule?.start ? new Date(c.schedule.start).toLocaleString() : "—"}
                        {c.schedule_conflicts.length > 0 && (
                          <span className="block text-xs text-amber-800"
                                data-testid={`change-conflict-${c.number}`}>
                            clashes with {c.schedule_conflicts.join(", ")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">{c.approval_request_state || "not requested"}</td>
                      <td className="px-3 py-2">
                        <Button size="sm" variant="ghost" data-testid={`change-open-${c.number}`}
                                onClick={() => setSelected(c)}>Open</Button>
                      </td>
                    </tr>
                  ))}
                  {board.changes.length === 0 && (
                    <tr><td colSpan="7" className="px-3 py-2 text-muted-foreground"
                            data-testid="change-board-empty">
                      No changes recorded.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {selected && (
          <section className="space-y-3 border-t pt-6" data-testid="change-detail">
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg font-medium" data-testid="change-detail-title">
                {selected.number} — {selected.title}
              </h2>
              <Button size="sm" variant="ghost" data-testid="change-detail-close"
                      onClick={() => setSelected(null)}>Close</Button>
            </div>
            <p className="text-sm" data-testid="change-detail-meta">
              {selected.state} · {selected.category} · risk {selected.risk_level || "unscored"}{" "}
              (score {selected.risk_score}) ·{" "}
              {selected.cab_required ? "CAB required" : "no CAB required"}
            </p>
            {selected.description && (
              <p className="text-sm whitespace-pre-wrap">{selected.description}</p>
            )}
            {selected.conflict_acknowledged && (
              <p className="text-sm text-amber-800" data-testid="change-detail-acknowledged">
                Window clash acknowledged by {selected.conflict_acknowledged.by_email} on{" "}
                {new Date(selected.conflict_acknowledged.at).toLocaleString()} for{" "}
                {selected.conflict_acknowledged.windows.join(", ")}
              </p>
            )}

            <div>
              <h3 className="text-sm font-semibold">Approvals</h3>
              <ul className="text-sm space-y-1" data-testid="change-approvals">
                {selected.approvals.map((a, i) => (
                  <li key={i}>
                    {a.approver_email || "approver"} — {a.decision || a.state || "pending"}
                  </li>
                ))}
                {selected.approvals.length === 0 && (
                  <li className="text-muted-foreground" data-testid="change-approvals-empty">
                    No approval request yet.
                  </li>
                )}
              </ul>
            </div>

            {selected.pending_ci_deltas.length > 0 && (
              <p className="text-sm text-amber-800" data-testid="change-pending-deltas">
                {selected.pending_ci_deltas.length} declared CI update(s) still unapplied — this
                change cannot close as successful until they are written.
              </p>
            )}

            {board?.can_author && (
              <div className="flex flex-wrap gap-2" data-testid="change-actions">
                <Button size="sm" variant="outline" data-testid="change-submit"
                        onClick={() => post(`/changes/${selected.id}/submit`, {},
                                             "Submitted for approval")}>
                  Submit for approval
                </Button>
                {selected.allowed_transitions.map((s) => (
                  <Button key={s} size="sm" variant="outline"
                          data-testid={`change-transition-${s}`}
                          onClick={() => post(`/changes/${selected.id}/transition`,
                                               { to_state: s }, `Moved to ${s}`)}>
                    {s}
                  </Button>
                ))}
                {selected.pending_ci_deltas.length > 0 && (
                  <Button size="sm" variant="outline" data-testid="change-apply-deltas"
                          onClick={() => post(`/changes/${selected.id}/apply-ci-updates`, {},
                                               "CI updates applied")}>
                    Apply CI updates
                  </Button>
                )}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
